import {
  template,
  UICorePlugin,
  type AttributeMap,
  type TemplateStringifier,
  type SupportedVersion,
  type EventMap
} from '@clappr/core'
import {type PromoButtonConfig, Position, ButtonSize} from 'common/api/records'
import type {Options} from 'common/types/options'
import {Events} from 'common/types/events'
import type {MediaControl} from 'common/plugins/media-control'
import {linkTemplate, template as spanTemplate} from './template'
import './styles.scss'

const HIDE_TIMEOUT = 10

export class PromoButton<T extends Options> extends UICorePlugin<T> {
  public get name(): string {
    return 'promoButton'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get attributes(): AttributeMap {
    return {
      class: 'eplayer-promo-button eplayer-skin-fullsize eplayer-skin-shiftable'
    }
  }

  public get events(): EventMap {
    return {
      'click [data-promo-button]': 'onPromoButtonClick'
    }
  }

  public get params(): PromoButtonConfig {
    return this.core.options.promoButton || ({} as PromoButtonConfig)
  }

  public get template(): TemplateStringifier {
    const pluginHtml = this.params.clickUrl ? linkTemplate : spanTemplate

    return template(pluginHtml)
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  public bindEvents(): void {
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_RENDERED,
        this.render
      )
    }

    const {activeContainer} = this.core

    if (activeContainer) {
      this.listenTo(activeContainer, Events.CONTAINER_PLAY, this.show)
      this.listenTo(activeContainer, Events.CONTAINER_SEEK, this.show)
      this.listenTo(activeContainer, Events.CONTAINER_ENDED, this.onEnded)
    }
  }

  public onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  private show(): void {
    this.$el.show()
  }

  private onEnded(): void {
    window.setTimeout(() => {
      // После окончания проигрывания, позиция меняется на 0 сек.
      // Поэтому отложим скрытие элемента, сделаем это уже после события `CONTAINER_SEEK`
      this.$el.hide()
    }, HIDE_TIMEOUT)
  }

  public onPromoButtonClick(): void {
    if (this.params.clickUrl) {
      this.trigger(Events.PROMO_BUTTON_CLICK)
    }
  }

  public render(): this {
    const {
      image: src,
      clickUrl: href,
      position = Position.RIGHT_DOWN,
      size
    } = this.params

    ;[
      Position.LEFT_UP,
      Position.LEFT_DOWN,
      Position.RIGHT_UP,
      Position.RIGHT_DOWN
    ].forEach((item) => {
      this.$el.toggleClass(`eplayer-promo-button-${item}`, item === position)
    })

    this.$el
      .toggleClass('eplayer-promo-button-large', size === ButtonSize.LARGE)
      .html(this.template({src, href}))
      .hide()

    this.mediaControl?.$el.append(this.el)

    return this
  }
}
